import React from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import {
    DATE_FNS_DATE_DISPLAY_FORMAT,
    C_TIMELINE_MIN_DATE,
    C_TIMELINE_MAX_DATE,
    MOMENTJS_DATE_DISPLAY_FORMAT,
    TIME_WINDOW_PRESETS,
    E_TIME_PRESET_KEYS,
} from '../../constants';
import { invoke } from 'lodash/fp';
import { dateOrMinAllowed, dateOrMaxAllowed } from '../../utils/generalUtils';
import PickerToggler from 'components/Pickers/PickerToggler';
import cx from 'classnames';

interface IGQDateRangePickerProps {
    initialStartDate?: Date;
    initialEndDate?: Date;
    onChange: (startDate: Date, endDate: Date) => void;
    endDateVisible: boolean;
    allowPresetSelectionOutOfRange?: boolean;
    maxDate?: Date;
    minDate?: Date;
    presets: E_TIME_PRESET_KEYS[];
    placement?: Placement;
    label?: string;
    trailingActions?: React.ReactNode;
}

interface IGQDateRangePickerState {
    isOpened: boolean;
    dateChangeNotReported: boolean;
    startDate: Date;
    endDate: Date;
    selectedPreset: E_TIME_PRESET_KEYS;
}

//todo: refactor with similar type
type Placement = 'top' | 'bottom';

export default class GQDateRangePicker extends React.Component<
    IGQDateRangePickerProps,
    IGQDateRangePickerState
> {
    private dateRangePickerRefNode: Node;

    public static defaultProps: Partial<IGQDateRangePickerProps> = {
        endDateVisible: true,
    };
    constructor(props: IGQDateRangePickerProps) {
        super(props);
        const startDate =
            props.initialStartDate ||
            moment()
                .subtract(90, 'days')
                .toDate();
        const endDate =
            props.initialEndDate ||
            moment()
                .add(90, 'days')
                .toDate();

        this.state = {
            isOpened: false,
            dateChangeNotReported: false,
            selectedPreset: null,
            startDate: dateOrMinAllowed(startDate, props.minDate),
            endDate: dateOrMaxAllowed(endDate, props.maxDate),
        };
    }

    public componentDidUpdate(prevProps: IGQDateRangePickerProps) {
        if (
            invoke('getTime', prevProps.initialStartDate) !==
                invoke('getTime', this.props.initialStartDate) ||
            invoke('getTime', prevProps.minDate) !==
                invoke('getTime', this.props.minDate) ||
            invoke('getTime', prevProps.initialEndDate) !==
                invoke('getTime', this.props.initialEndDate) ||
            invoke('getTime', prevProps.maxDate) !==
                invoke('getTime', this.props.maxDate)
        ) {
            let { startDate, endDate } = this.state;
            if (this.props.initialStartDate) {
                startDate = this.props.allowPresetSelectionOutOfRange
                    ? this.props.initialStartDate
                    : dateOrMinAllowed(
                          this.props.initialStartDate,
                          this.props.minDate
                      );
            }
            if (this.props.initialEndDate) {
                endDate = this.props.allowPresetSelectionOutOfRange
                    ? this.props.initialEndDate
                    : dateOrMaxAllowed(
                          this.props.initialEndDate,
                          this.props.maxDate
                      );
            }
            this.setState({
                startDate,
                endDate,
            });
        }
    }

    public render() {
        const label =
            this.props.label ||
            `${moment(this.state.startDate).format(
                MOMENTJS_DATE_DISPLAY_FORMAT
            )} - ${moment(this.state.endDate).format(
                MOMENTJS_DATE_DISPLAY_FORMAT
            )}`;
        return (
            <div style={{ position: 'relative' }} data-testid="date-range-picker">
                <PickerToggler
                    label={label}
                    icon="Calendar"
                    trailingActions={this.props.trailingActions}>
                    <div
                        className={cx(
                            'date-range-picker-wrapper',
                            this.props.placement
                        )}>
                        <div className="date-range-presets-wrapper">
                            <div className="date-range-presets">
                                {this.props.presets.map(preset => {
                                    return (
                                        <span
                                            data-testid="date-range-preset"
                                            className={`time-preset ${
                                                this.state.selectedPreset ===
                                                preset
                                                    ? 'selected'
                                                    : ''
                                            }`}
                                            key={preset}
                                            onClick={() =>
                                                this.onTimePresetSelected(
                                                    preset as E_TIME_PRESET_KEYS
                                                )
                                            }>
                                            {preset}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="calendars-wrapper">
                            <DatePicker
                                calendarClassName="start-date-picker"
                                inline
                                onChange={this.onStartDateChange}
                                selected={this.state.startDate}
                                selectsStart
                                startDate={this.state.startDate}
                                endDate={
                                    this.props.endDateVisible &&
                                    this.state.endDate
                                }
                                maxDate={
                                    this.props.maxDate ||
                                    C_TIMELINE_MAX_DATE.toDate()
                                }
                                minDate={
                                    this.props.minDate ||
                                    C_TIMELINE_MIN_DATE.toDate()
                                }
                                dateFormat={DATE_FNS_DATE_DISPLAY_FORMAT}
                                showYearDropdown
                                showMonthDropdown
                                peekNextMonth={false}
                                onClickOutside={this.handleClickOutside}
                            />
                            {this.props.endDateVisible && (
                                <DatePicker
                                    calendarClassName="end-date-picker"
                                    inline
                                    onChange={this.onEndDateChange}
                                    selected={this.state.endDate}
                                    selectsEnd
                                    startDate={this.state.startDate}
                                    endDate={this.state.endDate}
                                    minDate={moment(this.state.startDate)
                                        .add(1, 'days')
                                        .toDate()}
                                    maxDate={
                                        this.props.maxDate ||
                                        C_TIMELINE_MAX_DATE.toDate()
                                    }
                                    dateFormat={DATE_FNS_DATE_DISPLAY_FORMAT}
                                    showYearDropdown
                                    showMonthDropdown
                                    peekNextMonth={false}
                                    onClickOutside={this.handleClickOutside}
                                />
                            )}
                        </div>
                    </div>
                </PickerToggler>
            </div>
        );
    }

    private handleClickOutside = (e: any) => {
        if (!this.dateRangePickerRefNode?.contains(e.target as Node)) {
            this.closeDateRangePicker();
        }
    };

    private closeDateRangePicker = () => {
        if (this.state.dateChangeNotReported) {
            this.setState(
                {
                    isOpened: false,
                    dateChangeNotReported: false,
                },
                this.reportDateRangeChange
            );
        } else {
            this.setState({ isOpened: false });
        }
    };

    private onTimePresetSelected = (presetKey: E_TIME_PRESET_KEYS) => {
        const timePreset = TIME_WINDOW_PRESETS[presetKey];
        const date = moment()
            .add(timePreset.val, timePreset.type)
            .toDate();
        const isFutureDate = timePreset.val > 0;
        const today = moment().startOf('day').toDate();
        let startDate, endDate;
        if (isFutureDate) {
            startDate = today;
            endDate = date;
        } else {
            startDate = date;
            endDate = today;
        }
        this.setState(
            {
                startDate,
                endDate,
                dateChangeNotReported: false,
                selectedPreset: presetKey,
            },
            this.reportDateRangeChange
        );
    };

    private onStartDateChange = (startDate: Date) => {
        const endDate =
            startDate >= this.state.endDate
                ? moment(startDate)
                      .add(1, 'days')
                      .toDate()
                : this.state.endDate;
        this.setState({
            startDate,
            endDate,
            dateChangeNotReported: true,
            selectedPreset: null,
        });
    };

    private onEndDateChange = (endDate: Date) => {
        this.setState(
            {
                endDate,
                dateChangeNotReported: false,
                selectedPreset: null,
            },
            this.reportDateRangeChange
        );
    };

    private reportDateRangeChange() {
        this.props.onChange(this.state.startDate, this.state.endDate);
    }
}
